import { Link } from 'react-router-dom';

import { fmtAMD, fmtDate, fmtDateCompact, type Countdown } from '@/lib/format';
import { useI18n } from '@/providers/i18n';
import ImgSlot from '@/ui/ImgSlot';
import type { MoctEvent } from '@/types';

import styles from './SignalFeature.module.css';

interface SignalFeatureProps {
  event: MoctEvent;
  countdown: Countdown;
}

export default function SignalFeature({ event, countdown }: SignalFeatureProps) {
  const { t, lang } = useI18n();
  const d = fmtDate(event.date, lang);

  return (
    <section className={styles.root}>
      <div className={styles.poster}>
        <ImgSlot src={event.posterUrl} alt={event.title} label={`${event.code} · POSTER`} />
        <div className={styles.crosshair} />
        <div className={styles.stamp}>
          <span>{event.code}</span>
          <span>{fmtDateCompact(event.date)}</span>
          <span>HAYFILM CLUSTER · 4 STAGES</span>
        </div>
        <div className={styles.stampRight}>
          <span className="plus plus-lg" style={{ color: 'var(--fg)', marginRight: 6 }}>
            +
          </span>{' '}
          FEATURED · ( SEVEN YEARS )
        </div>
        <div className={`${styles.countdown} breathe-slow`}>
          <small>{t('label.next')}</small>
          T-{countdown.d}:{countdown.h}:{countdown.m}:{countdown.s}
        </div>
      </div>
      <div className={styles.meta}>
        <span className="nano">M / 001 / FLAGSHIP</span>
        <h2 className={styles.title}>{event.title}</h2>
        <div className={styles.tag}>{event.subtitle}</div>
        <div className={styles.rows}>
          <div className={styles.row}>
            <label>{t('label.date')}</label>
            <span>
              {d.day} {d.month} {d.year}
            </span>
          </div>
          <div className={styles.row}>
            <label>{t('label.doors')}</label>
            <span>{event.doors}</span>
          </div>
          <div className={styles.row}>
            <label>{t('label.venue')}</label>
            <span>HAYFILM CLUSTER</span>
          </div>
          <div className={styles.row}>
            <label>{t('label.capacity')}</label>
            <span>{event.capacity}</span>
          </div>
        </div>
        <div className={styles.tiers}>
          {event.tiers.map((tier) => (
            <div
              key={tier.id}
              className={styles.tier}
              data-soldout={tier.remaining === 0 ? 'true' : 'false'}
            >
              <span className="dot" />
              <span>{tier.name}</span>
              <span className="note fg-dim">{tier.note}</span>
              <span className="price">{fmtAMD(tier.price)}</span>
            </div>
          ))}
        </div>
        <div className={styles.actions}>
          <Link to={`/tickets/${event.id}`} className="btn btn-fill btn-arrow">
            {t('cta.buy')}
          </Link>
          <Link to={`/events/${event.id}`} className="btn btn-ghost">
            {t('cta.lineup')}
          </Link>
        </div>
      </div>
    </section>
  );
}
